import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Reservation,
  Store,
  StoreReservationSettings,
} from "@/models/storeModel";
import { defaultOperatingHours } from "@/utils/constants";
import { sendReservationConfirmation } from "../notification/notificationService";
import { sendReservationToStore } from "../notification/notificationService";

export const createReservation = async (
  reservation: Omit<Reservation, "id" | "created_at" | "updated_at">
): Promise<Reservation | null> => {
  try {
    const { data, error } = await supabase
      .from("reservations")
      .insert([reservation])
      .select()
      .single();

    if (error) {
      console.error("Error creating reservation:", error);
      return null;
    }
    // const confirmationData = {
    //   ...data,
    //   store_name: store?.name || "Any Store",
    //   table_name: reservation.table_id || "Any Table", // Or fetch actual table name
    //   booking_url: window.location.href,
    //   store_email: store?.email || "Any Store Email",
    //   dashboard_url: window.location.href,
    // };
    // console.log("Sending2 reservation confirmation....:", confirmationData);
    // await sendReservationConfirmation(confirmationData);
    // await sendReservationToStore(confirmationData);

    return data as Reservation;
  } catch (error) {
    console.error("Error creating reservation:", error);
    return null;
  }
};

export const fetchReservations = async (
  storeId: string
): Promise<Reservation[]> => {
  try {
    const { data, error } = await supabase
      .from("reservations")
      .select(
        `
        *,
        store_tables:table_id(name, number)
      `
      )
      .eq("store_id", storeId)
      .order("reservation_date", { ascending: true })
      .order("reservation_time", { ascending: true });

    if (error) {
      console.error("Error fetching reservations:", error);
      return [];
    }

    return (data || []).map((item) => ({
      ...item,
      status: item.status as "confirmed" | "cancelled" | "completed",
    }));
  } catch (error) {
    // console.error("Error in fetchReservations:", error);
    return [];
  }
};

export const updateReservation = async (
  id: string,
  updates: Partial<Reservation>
): Promise<Reservation | null> => {
  try {
    const { data, error } = await supabase
      .from("reservations")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Error updating reservation:", error);
      return null;
    }

    return data as Reservation;
  } catch (error) {
    console.error("Error updating reservation:", error);
    return null;
  }
};

export const deleteReservation = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase.from("reservations").delete().eq("id", id);

    if (error) {
      console.error("Error deleting reservation:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error deleting reservation:", error);
    return false;
  }
};

export const fetchReservationSettings = async (
  storeId: string
): Promise<StoreReservationSettings | null> => {
  try {
    const { data, error } = await supabase
      .from("store_reservation_settings")
      .select()
      .eq("store_id", storeId)
      .maybeSingle();

    if (!data) return null;

    if (data.operating_hours && typeof data.operating_hours === "string") {
      data.operating_hours = JSON.parse(data.operating_hours);
    }

    return data as StoreReservationSettings;
  } catch (error) {
    console.error("Error in fetchReservationSettings:", error);
    return null;
  }
};

export const generateStoreSlug = (storeId: string) => {
  // Create a shorter random string (first 8 chars of UUID)
  return `store-${storeId}`;
};

export const createReservationSettings = async (
  storeId: string,
  settings: Partial<StoreReservationSettings>
): Promise<StoreReservationSettings | null> => {
  try {
    const slug = generateStoreSlug(storeId);
    const publicSlug = slug;
    // First check if settings already exist
    const { data: existingSettings } = await supabase
      .from("store_reservation_settings")
      .select()
      .eq("store_id", storeId)
      .single();

    // If settings exist, use update instead
    if (existingSettings) {
      return updateReservationSettings(storeId, settings);
    }

    const { data, error } = await supabase
      .from("store_reservation_settings")
      .insert([
        {
          store_id: storeId,
          public_slug: publicSlug,
          is_public_booking_enabled: settings.is_public_booking_enabled ?? true,
          operating_hours: JSON.stringify(
            settings.operating_hours || defaultOperatingHours
          ),
          max_party_size: settings.max_party_size ?? 10,
          booking_window_days: settings.booking_window_days ?? 30,
        },
      ])
      .select()
      .single();

    if (error) {
      console.error("Error creating reservation settings:", error);
      if (error.code === "23505" && error.message.includes("public_slug")) {
        // Only handle duplicate slug, not duplicate store_id
        const uniqueSlug = `${publicSlug}-${Date.now()}`;
        return createReservationSettings(storeId, {
          ...settings,
          public_slug: uniqueSlug,
        });
      }
      return null;
    }

    return data as StoreReservationSettings;
  } catch (error) {
    console.error("Error in createReservationSettings:", error);
    return null;
  }
};

export const extractStoreIdFromSlug = (slug: string): string | null => {
  if (!slug) return null;

  // Handle both formats:
  // 1. store-{id}
  // 2. {id}
  if (slug.startsWith("store-")) {
    return slug.slice("store-".length);
  }

  // If it's just the ID, return it directly
  return slug;
};

export const updateReservationSettings = async (
  storeId: string,
  settings: Partial<StoreReservationSettings>
): Promise<StoreReservationSettings | null> => {
  try {
    const slug = generateStoreSlug(storeId);
    console.log("slug...existingWithSlug", slug);
    settings.public_slug = slug;

    const { data, error } = await supabase
      .from("store_reservation_settings")
      .update({
        ...settings,
        operating_hours: settings.operating_hours
          ? JSON.stringify(settings.operating_hours)
          : undefined,
      })
      .eq("store_id", storeId)
      .select()
      .single();

    if (error) {
      console.error("Error updating reservation settings:", error);
      return null;
    }

    return data as StoreReservationSettings;
  } catch (error) {
    console.error("Error in updateReservationSettings:", error);
    return null;
  }
};

export const fetchReservationSettingsBySlug = async (
  slug: string
): Promise<{ settings: StoreReservationSettings; store: any } | null> => {
  try {
    const { data, error } = await supabase
      .from("store_reservation_settings")
      .select(
        `
        *,
        stores:store_id(*)
      `
      )
      .eq("public_slug", slug)
      .eq("is_public_booking_enabled", true)
      .single();

    if (error) {
      console.error("Error fetching reservation settings by slug:", error);
      return null;
    }

    // Handle operating_hours that could be either string or object
    const operating_hours =
      typeof data.operating_hours === "string"
        ? JSON.parse(data.operating_hours)
        : data.operating_hours;

    return {
      settings: {
        ...data,
        operating_hours,
      },
      store: data.stores,
    };
  } catch (error) {
    console.error("Error fetching reservation settings by slug:", error);
    return null;
  }
};
